import React, { useEffect, useState } from "react";
import * as Bananas from "bananas-commerce-admin";

import { Add } from "@mui/icons-material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";

import dayjs from "dayjs";
import { useSnackbar } from "notistack";

import {
  DetailedPublicationSchema,
  PricesGroupSchema,
  PublicationPricesPerDaySchema,
} from "../../types";
import { GAP } from "../../utils";
import DateField from "../DateField";
import NewPriceListDialog from "../NewPriceListDialog";

import AddPublicationDialog from "./AddPublicationDialog";
import CloseConfirmationDialog from "./CloseConfirmationDialog";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import PriceListRow from "./PriceListRow";
import { PublicationDaysManager } from "./PublicationDaysManager";
import {
  PricesGroupEditable,
  PublicationPricesPerDayEditable,
  toPublicationPricesPerDay,
  toPublicationPricesPerDayForTracking,
} from "./types";

export type PriceListStatus = "aktiv" | "passerad";

export const statusColor: Record<PriceListStatus, readonly [string, string]> = {
  aktiv: ["#C8E6C9", "#388E3C"],
  passerad: ["#E0E0E0", "#616161"],
} as const;

export interface PriceListCardProps {
  data: PricesGroupSchema;
  publications: DetailedPublicationSchema[];
  refresh: () => void;
  defaultExpanded?: boolean;
  initialEditing?: boolean;
  initialAdding?: boolean;
  onCopy?: (data: PricesGroupSchema) => void;
}

const PriceListCard: React.FC<PriceListCardProps> = ({
  defaultExpanded = false,
  data: initialData,
  publications: publicationsProp,
  refresh,
  initialEditing = false,
  initialAdding = false,
  onCopy,
}) => {
  // Add _editId to existing items if they don't have one
  const initializeEditIds = (data: PricesGroupSchema): PricesGroupEditable => {
    return {
      ...data,
      publications_prices: data.publications_prices.map((pub, idx) => ({
        ...pub,
        _editId: `${pub.publication}-existing-${idx}`,
      })),
    };
  };

  const [data, setData] = useState<PricesGroupEditable>(
    initializeEditIds(initialData),
  );
  const [savedData, setSavedData] = useState<PricesGroupEditable>(
    initializeEditIds(initialData),
  );
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [isEditing, setIsEditing] = useState(initialEditing);
  const [isAdding, setIsAdding] = useState(initialAdding);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);

  const { t } = Bananas.useI18n();
  const api = Bananas.useApi();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const today = dayjs();
  const toDate = dayjs(data.to_date);

  const status: PriceListStatus = today.isAfter(toDate) ? "passerad" : "aktiv";

  const [showAddDialog, setShowAddDialog] = useState(false);

  const handleAddRow = (
    publicationAbbreviation?: PublicationPricesPerDaySchema["publication"],
  ) => {
    if (!publicationAbbreviation) {
      setShowAddDialog(true);
      return;
    }

    const daysManager = new PublicationDaysManager(
      data.publications_prices as PublicationPricesPerDaySchema[],
    );
    const availableDays = daysManager.getAvailableDays(publicationAbbreviation);

    const newPublication: PublicationPricesPerDayEditable = {
      publication: publicationAbbreviation,
      days_prices: availableDays.map((weekday) => ({
        weekday,
        simple_width: undefined,
        double_width: undefined,
        memorial: undefined,
        thanks: undefined,
        discount_pct: undefined,
      })),
      _editId: `${publicationAbbreviation}-${Date.now()}-${Math.random()}`,
    };
    setData({
      ...data,
      publications_prices: [...data.publications_prices, newPublication],
    });
    setShowAddDialog(false);
  };

  const handleAddAllPublications = () => {
    const daysManager = new PublicationDaysManager(
      data.publications_prices as PublicationPricesPerDaySchema[],
    );
    const availablePublications = publicationsProp.filter((pub) =>
      daysManager.hasAvailableDays(pub.abbreviation),
    );

    const newPublications: PublicationPricesPerDayEditable[] =
      availablePublications.map((pub, idx) => {
        const availableDays = daysManager.getAvailableDays(pub.abbreviation);
        return {
          publication: pub.abbreviation,
          days_prices: availableDays.map((weekday) => ({
            weekday,
            simple_width: undefined,
            double_width: undefined,
            memorial: undefined,
            thanks: undefined,
            discount_pct: undefined,
          })),
          _editId: `${pub.abbreviation}-${Date.now()}-${idx}`,
        };
      });

    setData({
      ...data,
      publications_prices: [...data.publications_prices, ...newPublications],
    });
    setShowAddDialog(false);
  };

  const handleRemoveRow = (index: number) => {
    const newData = [...data.publications_prices];
    newData.splice(index, 1);
    setData({
      ...data,
      publications_prices: newData,
    });
  };

  const handleRemoveGroup = (index: number, daysToRemove: string[]) => {
    const newData = [...data.publications_prices];
    const currentRow = newData[index];

    if (!currentRow) return;

    // Filter out the days to remove
    const remainingDays = currentRow.days_prices.filter(
      (day) => !daysToRemove.includes(day.weekday),
    );

    // If no days remain, remove the entire row
    if (remainingDays.length === 0) {
      newData.splice(index, 1);
    } else {
      // Otherwise, update the row with remaining days
      newData[index] = {
        ...currentRow,
        days_prices: remainingDays,
      };
    }

    setData({
      ...data,
      publications_prices: newData,
    });
  };

  const handleSave = async () => {
    const publicationMap = new Map<
      PublicationPricesPerDaySchema["publication"],
      PublicationPricesPerDaySchema
    >();

    for (const _item of data.publications_prices) {
      const item = toPublicationPricesPerDay(_item);
      if (!item) {
        enqueueSnackbar("Alla prisfält måste fyllas i med ett värde", {
          variant: "error",
        });
        return;
      }

      const existing = publicationMap.get(item.publication);
      if (existing) {
        const uniqueDays = [...existing.days_prices];
        for (const day of item.days_prices) {
          if (!uniqueDays.some((d) => d.weekday === day.weekday)) {
            uniqueDays.push(day);
          }
        }
        publicationMap.set(item.publication, {
          ...item,
          days_prices: uniqueDays,
        });
      } else {
        publicationMap.set(item.publication, item);
      }
    }

    const publicationsPrices = Array.from(publicationMap.values()).map(
      (item) => {
        return {
          ...item,
          days_prices: item.days_prices.filter((day) => day.weekday),
        };
      },
    );

    if (publicationsPrices.some((item) => !item.publication)) {
      enqueueSnackbar("Rad med tom publikationsfält hittades", {
        variant: "error",
      });
      return;
    }

    if (publicationsPrices.some((item) => item.days_prices.length === 0)) {
      enqueueSnackbar("Rad med inga dagar valda hittades", {
        variant: "error",
      });
      return;
    }

    // Double check.
    const hasEmptyPriceFields = publicationsPrices.some((item) =>
      item.days_prices.some(
        (day) =>
          day.simple_width == null ||
          day.double_width == null ||
          day.memorial == null ||
          day.thanks == null ||
          day.discount_pct == null ||
          isNaN(day.simple_width) ||
          isNaN(day.double_width) ||
          isNaN(day.memorial) ||
          isNaN(day.thanks) ||
          isNaN(day.discount_pct),
      ),
    );
    if (hasEmptyPriceFields) {
      enqueueSnackbar("Alla prisfält måste fyllas i med ett värde", {
        variant: "error",
      });
      return;
    }

    const newData: PricesGroupSchema = {
      from_date: data.from_date,
      to_date: data.to_date,
      publications_prices: publicationsPrices,
    };

    try {
      const operation = isAdding
        ? api.operations["mediahouse.prices:create"]
        : api.operations["mediahouse.prices:edit"];
      if (!operation) {
        throw new Error(
          isAdding
            ? 'No action found for "mediahouse.prices:create"'
            : 'No action found for "mediahouse.prices:edit"',
        );
      }

      if (!isAdding && initialData.group_id == null)
        throw new Error("No group id found");

      await operation.call({
        body: newData,
        params: {
          group_id: initialData.group_id,
        },
      });
      setIsAdding(false);
      setIsEditing(false);
      setSavedData(data);
      refresh();
    } catch (error) {
      console.error("Failed to update prices:", error);
      // TODO Add error handling/notification
    }
  };

  const hasUnsavedChanges = () => {
    return JSON.stringify(data) !== JSON.stringify(savedData);
  };

  const handleClose = () => {
    const hasChanges = hasUnsavedChanges();

    if (hasChanges) {
      setIsCloseDialogOpen(true);
    } else {
      setIsEditing(false);
    }
  };

  const handleCloseConfirm = () => {
    setIsCloseDialogOpen(false);
    if (isAdding) {
      // If this is a new price list being added, remove it entirely
      refresh();
    } else {
      // If editing existing, revert to saved state
      setData(savedData);
      setIsEditing(false);
    }
  };

  const handleCloseCancel = () => {
    setIsCloseDialogOpen(false);
  };

  const handleStartEditing = () => {
    setSavedData(data);
    setIsEditing(true);
  };

  const handleRowUpdate = (
    index: number,
    updatedData: PublicationPricesPerDayEditable,
  ) => {
    const newData = [...data.publications_prices];
    newData[index] = updatedData;
    setData({
      ...data,
      publications_prices: newData,
    });
  };

  const handleCopy = () => {
    setIsCopyDialogOpen(true);
  };

  const handleCopyConfirm = async (fromDate: string, toDate: string) => {
    try {
      const operation = api.operations["mediahouse.prices:create"];
      if (!operation) {
        throw new Error('No action found for "mediahouse.prices:create"');
      }

      const newData = {
        ...data,
        from_date: fromDate,
        to_date: toDate,
        group_id: undefined,
      };

      const response = await operation.call({
        body: newData,
      });
      const result = await response.json();
      setIsCopyDialogOpen(false);
      if (onCopy) {
        onCopy(result);
      }
      refresh();
    } catch (error) {
      console.error("Failed to copy prices:", error);
      // TODO Add error handling/notification
    }
  };

  const handleCopyCancel = () => {
    setIsCopyDialogOpen(false);
  };

  const handleDelete = async () => {
    try {
      const operation = api.operations["mediahouse.prices:delete"];
      if (!operation) {
        throw new Error('No action found for "mediahouse.prices:delete"');
      }

      await operation.call({
        params: {
          group_id: initialData.group_id,
        },
      });
      refresh();
    } catch (error) {
      console.error("Failed to delete prices:", error);
      // TODO Add error handling/notification
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setIsDeleteDialogOpen(false);
    handleDelete();
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
  };

  useEffect(() => {
    const initialized = initializeEditIds(initialData);
    setData(initialized);
    setSavedData(initialized);
  }, [initialData]);

  // Warn user before closing tab if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isEditing && hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isEditing, data, savedData]);

  // Initialize with all publications only when adding new price list
  useEffect(() => {
    if (
      isAdding &&
      data.publications_prices.length === 0 &&
      publicationsProp.length > 0
    ) {
      const allWeekdays = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];
      const allPublicationsPrices: PublicationPricesPerDayEditable[] =
        publicationsProp.map((pub, idx) => ({
          publication: pub.abbreviation,
          days_prices: allWeekdays.map((weekday) => ({
            weekday,
            simple_width: undefined,
            double_width: undefined,
            memorial: undefined,
            thanks: undefined,
            discount_pct: undefined,
          })),
          _editId: `${pub.abbreviation}-init-${idx}`,
        }));
      setData({
        ...data,
        publications_prices: allPublicationsPrices,
      });
    }
  }, [isAdding, publicationsProp]);

  return (
    <Card
      sx={{
        borderRadius: 2,
      }}
      variant="outlined"
    >
      <CardContent sx={{ p: 0, pb: "0 !important" }}>
        <Stack
          alignItems="center"
          borderBottom={1}
          borderColor="divider"
          direction="row"
          justifyContent="space-between"
          p={2}
        >
          <Stack alignItems="center" direction="row" gap={2}>
            <Chip
              label={status}
              size="small"
              sx={{
                textTransform: "uppercase",
                bgcolor: statusColor[status][0],
                color: statusColor[status][1],
                height: theme.spacing(3),
                fontSize: theme.typography.pxToRem(12),
                width: 100,
              }}
            />
            <Typography fontWeight={500} variant="body1">
              {data.from_date} - {data.to_date}
            </Typography>
          </Stack>
          <IconButton
            size="small"
            sx={{
              transform: expanded ? "none" : "rotate(180deg)",
              transition: theme.transitions.create("transform"),
            }}
            onClick={() => setExpanded(!expanded)}
          >
            <KeyboardArrowUpIcon />
          </IconButton>
        </Stack>

        <Collapse in={expanded}>
          <Box>
            <Box
              sx={{
                bgcolor:
                  theme.palette.mode === "dark" ? "grey.900" : "grey.100",
              }}
            >
              {isEditing && (
                <Stack gap={1} p={GAP}>
                  <Typography gutterBottom variant="subtitle1">
                    {t("Period")}
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Stack>
                      <Typography
                        color="text.secondary"
                        fontWeight={500}
                        sx={{ mb: 1 }}
                        variant="body2"
                      >
                        {t("Från")}
                      </Typography>

                      <DateField
                        error={Boolean(false)}
                        value={
                          initialData.from_date
                            ? dayjs(initialData.from_date)
                            : null
                        }
                        onChange={(newDate) => {
                          setData({
                            ...data,
                            from_date: newDate?.format("YYYY-MM-DD") ?? "",
                          });
                        }}
                      />
                    </Stack>
                    <Typography color="text.secondary" pt={4.5} variant="body2">
                      —
                    </Typography>
                    <Stack>
                      <Typography
                        color="text.secondary"
                        fontWeight={500}
                        sx={{ mb: 1 }}
                        variant="body2"
                      >
                        {t("Till")}
                      </Typography>
                      <DateField
                        error={Boolean(false)}
                        value={
                          initialData.to_date
                            ? dayjs(initialData.to_date)
                            : null
                        }
                        onChange={(newDate) => {
                          setData({
                            ...data,
                            to_date: newDate?.format("YYYY-MM-DD") ?? "",
                          });
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              )}
              <Divider />
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "20%" }}>
                      <Typography variant="body2">Titel</Typography>
                    </TableCell>
                    <TableCell sx={{ width: "20%" }}>
                      <Typography variant="body2">Dagar</Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", width: "12%" }}>
                      <Typography variant="body2">
                        1-Spalt Dödsannons
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", width: "12%" }}>
                      <Typography variant="body2">
                        2-Spalt Dödsannons
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", width: "12%" }}>
                      <Typography variant="body2">Minnesannons</Typography>
                    </TableCell>
                    <TableCell sx={{ width: "12%" }}>
                      <Typography variant="body2">Tackannons</Typography>
                    </TableCell>
                    <TableCell sx={{ width: "12%" }}>
                      <Typography variant="body2">Rabatt</Typography>
                    </TableCell>
                    {isEditing && <TableCell sx={{ width: "100px" }} />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.publications_prices.map((pub, index) => {
                    const otherRowsSelectedDays = data.publications_prices
                      .filter(
                        (p, i) =>
                          i !== index && p.publication === pub.publication,
                      )
                      .flatMap((p) => p.days_prices.map((d) => d.weekday));

                    const uniqueKey = pub._editId || `fallback-${index}`;

                    return (
                      <PriceListRow
                        key={uniqueKey}
                        data={pub}
                        disabledDays={otherRowsSelectedDays}
                        isEditing={isEditing}
                        isLoadingPublications={false}
                        publications={publicationsProp}
                        onRemove={() => handleRemoveRow(index)}
                        onRemoveGroup={(daysToRemove) =>
                          handleRemoveGroup(index, daysToRemove)
                        }
                        onUpdate={(updatedData) =>
                          handleRowUpdate(index, updatedData)
                        }
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Box>

          {isEditing ? (
            <Box
              bgcolor={theme.palette.mode === "dark" ? "grey.900" : "grey.100"}
              px={GAP}
              py={GAP / 1.5}
            >
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Button
                  color="secondary"
                  disabled={(() => {
                    if (publicationsProp.length === 0) return true;
                    const publicationPrices = data.publications_prices.flatMap(
                      (e) => {
                        const item = toPublicationPricesPerDayForTracking(e);
                        return item ? [item] : [];
                      },
                    );
                    const daysManager = new PublicationDaysManager(
                      publicationPrices,
                    );
                    return daysManager.areAllPublicationsFull(
                      publicationsProp.map((p) => p.abbreviation),
                    );
                  })()}
                  startIcon={<Add />}
                  variant="text"
                  onClick={() => handleAddRow()}
                >
                  {t("Lägg till")}
                </Button>
                <Stack direction="row" spacing={1}>
                  <Button variant="text" onClick={handleClose}>
                    {t("Stäng")}
                  </Button>
                  <Button variant="outlined" onClick={handleSave}>
                    {t("Spara")}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          ) : (
            <Box
              bgcolor={theme.palette.mode === "dark" ? "grey.900" : "white"}
              p={GAP}
            >
              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <Button variant="text" onClick={handleDeleteClick}>
                  {t("Ta bort")}
                </Button>
                <Button variant="text" onClick={handleCopy}>
                  {t("Kopiera")}
                </Button>
                <Button variant="outlined" onClick={handleStartEditing}>
                  {t("Ändra")}
                </Button>
              </Stack>
            </Box>
          )}
        </Collapse>
      </CardContent>
      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
      <CloseConfirmationDialog
        isDeleting={isAdding}
        open={isCloseDialogOpen}
        onClose={handleCloseCancel}
        onConfirm={handleCloseConfirm}
      />
      <NewPriceListDialog
        isCopying={true}
        open={isCopyDialogOpen}
        onClose={handleCopyCancel}
        onSubmit={handleCopyConfirm}
      />
      <AddPublicationDialog
        isLoadingPublications={false}
        open={showAddDialog}
        publications={publicationsProp}
        publicationsPrices={data.publications_prices}
        onAdd={(abbreviation) => handleAddRow(abbreviation)}
        onAddAll={handleAddAllPublications}
        onClose={() => setShowAddDialog(false)}
      />
    </Card>
  );
};

export default PriceListCard;
